"use client";

import React, { useState } from "react";
import { ArrowRight, BookOpen, Star, MapPin, GraduationCap, Guitar, CheckCircle2, ChevronDown } from "lucide-react";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const SECTIONS = [
  {
    id: "guitar-cu",
    icon: Guitar,
    tag: "01",
    heading: "Tại Sao Nên Chọn Guitar Cũ?",
    body: [
      "Với người mới bắt đầu học guitar, câu hỏi đầu tiên thường là: <em>mua đàn mới hay đàn cũ?</em> Câu trả lời phụ thuộc vào ngân sách, mục tiêu và giai đoạn học. Tuy nhiên, trong phần lớn các trường hợp, <strong>guitar cũ là lựa chọn thông minh hơn</strong> — nếu bạn biết cách chọn.",
      "Khi mua guitar mới ở mức giá thấp (dưới 1–2 triệu), bạn thường nhận được cây đàn với chất liệu gỗ kém, cần đàn dễ cong và âm thanh khô cứng. Trong khi đó, cùng mức ngân sách đó, một cây guitar đã qua sử dụng từ thương hiệu uy tín như Yamaha, Takamine hay Morris có thể cho bạn chất lượng âm thanh vượt trội — vì gỗ đã \"qua lửa\", cộng hưởng ổn định hơn và tông đàn rõ ràng hơn nhiều.",
      "Ngoài ra, <strong>guitar cũ đã được \"thuần hóa\"</strong> — cần đàn đã ổn định theo thời gian, phím đàn đã mòn đều tự nhiên, và người chơi trước đó đã thiết lập action phù hợp. Điều này có nghĩa là ngay từ buổi đầu tiên, bạn có thể chơi thoải mái hơn nhiều so với một cây đàn mới chưa được setup.",
    ],
    highlights: [
      "Giá trị tốt hơn cùng mức ngân sách",
      "Gỗ ổn định – âm thanh chín hơn",
      "Đã qua kiểm tra & thiết lập thực tế",
    ],
  },
  {
    id: "loi-ich",
    icon: Star,
    tag: "02",
    heading: "Lợi Ích Của Guitar Đã Qua Sử Dụng",
    body: [
      "Nhiều người ngại mua <strong>guitar đã qua sử dụng</strong> vì lo ngại về chất lượng — nhưng đây thực ra là một hiểu lầm phổ biến. Khi được kiểm tra kỹ và mua từ nguồn tin cậy, guitar cũ mang lại nhiều lợi ích mà đàn mới cùng giá không thể có.",
      "<strong>Thứ nhất, giảm khấu hao giá trị.</strong> Một cây guitar mới vừa ra khỏi cửa hàng có thể mất 20–30% giá trị ngay lập tức. Guitar cũ đã qua giai đoạn khấu hao này, giúp bạn bán lại hoặc trao đổi về sau mà không mất quá nhiều.",
      "<strong>Thứ hai, dễ dàng nâng cấp.</strong> Khi bạn tiến bộ, nhu cầu về âm thanh và kỹ thuật thay đổi. Guitar cũ giúp bạn dễ trao đổi lên cây đàn phù hợp hơn — thay vì mắc kẹt với một cây đàn mới đắt tiền không còn phù hợp với trình độ hiện tại.",
      "<strong>Thứ ba, tiết kiệm ngân sách cho phụ kiện và học phí.</strong> Tiền dư từ việc chọn đàn cũ có thể đầu tư vào bao đàn chất lượng, dây đàn tốt, capo, hoặc quan trọng hơn — vào khóa học guitar để tiến bộ nhanh hơn.",
    ],
    highlights: [
      "Ít khấu hao – dễ bán lại",
      "Linh hoạt nâng cấp theo trình độ",
      "Tiết kiệm ngân sách dài hạn",
    ],
  },
  {
    id: "kinh-nghiem-tphcm",
    icon: BookOpen,
    tag: "03",
    heading: "Kinh Nghiệm Mua Guitar Ở TPHCM",
    body: [
      "Thị trường <strong>mua guitar ở TPHCM</strong> rất đa dạng — từ các cửa hàng lớn ở quận trung tâm đến các cửa hàng chuyên biệt ở ngoại thành. Tuy nhiên, không phải nơi nào cũng minh bạch về tình trạng đàn, đặc biệt với guitar cũ.",
      "Kinh nghiệm đúc kết từ hàng trăm giao dịch: <strong>đừng mua guitar cũ qua ảnh</strong>. Ảnh chụp có thể che giấu vết nứt nhỏ, lỗi cần đàn hay phím mòn không đều. Luôn yêu cầu được kiểm tra trực tiếp hoặc mang đến cửa hàng uy tín để kiểm tra trước khi quyết định.",
      "Một số điểm cần kiểm tra khi <strong>mua guitar cũ ở TPHCM</strong>: độ thẳng của cần đàn (nhìn dọc từ đầu về body), action tại phím 12 (không quá cao hay quá thấp), tình trạng phím đàn (mòn đều hay có vết lõm sâu), và quan trọng nhất — <strong>âm thanh thực tế khi chơi</strong> ở nhiều vị trí phím khác nhau.",
      "Tại Guitar Sài Gòn, mọi cây đàn cũ đều trải qua quy trình kiểm tra 10 bước trước khi bày bán. Khách hàng nhận được báo cáo tình trạng rõ ràng và bảo hành 3 tháng — điều hiếm thấy ở thị trường guitar cũ TPHCM hiện nay.",
    ],
    highlights: [
      "Không mua qua ảnh – phải xem trực tiếp",
      "Kiểm tra cần đàn, action, phím, âm thanh",
      "Yêu cầu bảo hành rõ ràng trước khi mua",
    ],
  },
  {
    id: "quan-12",
    icon: MapPin,
    tag: "04",
    heading: "Mua Guitar Ở Quận 12 Có Gì Thuận Tiện?",
    body: [
      "Nhiều người chơi guitar tại khu vực phía Bắc TPHCM — bao gồm Quận 12, Gò Vấp, Hóc Môn, Bình Dương và Thủ Đức — thường phải di chuyển xa vào trung tâm để tìm mua đàn chất lượng. Điều này tốn thời gian và không tiện cho việc kiểm tra, bảo hành sau mua.",
      "<strong>Guitar Sài Gòn tại Quận 12</strong> được xây dựng để phục vụ chính xác nhóm khách hàng này. Địa chỉ tại 73 Đường ĐHT 31, Phường Tân Hưng Thuận giúp khách từ Gò Vấp, Hóc Môn, Bình Dương đến dễ dàng mà không cần vào trung tâm thành phố.",
      "Ngoài ra, việc <strong>mua guitar gần nhà</strong> còn mang lại lợi thế lớn trong việc bảo hành và hỗ trợ kỹ thuật. Khi dây đàn cần thay, cần đàn cần chỉnh, hay bạn muốn setup lại action — bạn chỉ cần ghé qua thay vì gửi xe đường dài.",
      "Với khách hàng tại <strong>Quận 12, Gò Vấp và Hóc Môn</strong>, Guitar Sài Gòn còn hỗ trợ tư vấn và đặt lịch kiểm tra đàn linh hoạt theo giờ mở cửa 18h–22h — phù hợp với người đi làm ban ngày.",
    ],
    highlights: [
      "Gần khách vùng Bắc TPHCM & Bình Dương",
      "Hỗ trợ bảo hành tại chỗ, không cần đi xa",
      "Giờ mở cửa 18h–22h – thuận tiện sau giờ làm",
    ],
  },
  {
    id: "khoa-hoc",
    icon: GraduationCap,
    tag: "05",
    heading: "Kết Hợp Khóa Học Guitar Để Tiến Bộ Nhanh",
    body: [
      "Mua được cây đàn tốt mới chỉ là bước đầu. Điều thực sự quyết định bạn có chơi được guitar hay không — chính là <strong>phương pháp học</strong>. Nhiều người tự học qua YouTube nhiều tháng nhưng vẫn bị sai kỹ thuật tay phải, tay trái không đúng vị trí, và quan trọng nhất — không biết mình đang sai ở đâu.",
      "Đó là lý do khóa học guitar 1 kèm 1 ra đời. Khi có giáo viên ngồi cùng, từng động tác được sửa ngay lập tức. <strong>Tiến bộ nhanh gấp 2–3 lần so với tự học</strong> — không phải vì học nhiều hơn, mà vì học đúng hơn từ đầu.",
      "Tại Guitar Sài Gòn, <strong>khóa học guitar tại Quận 12</strong> được thiết kế cá nhân theo trình độ và mục tiêu từng học viên. Người muốn đệm hát sẽ có lộ trình khác với người muốn học solo hay nhạc lý. Lịch học linh hoạt, buổi học thử miễn phí — để bạn trải nghiệm trước khi quyết định.",
    ],
    highlights: [
      "Sửa kỹ thuật ngay từ buổi đầu tiên",
      "Lộ trình cá nhân hóa theo mục tiêu",
      "Học thử miễn phí – không cam kết trước",
    ],
  },
  {
    id: "cuong-guitar",
    icon: Star,
    tag: "06",
    heading: "Vai Trò Của Cường Guitar Trong Việc Hướng Dẫn Người Mới",
    body: [
      "Cường Guitar không chỉ là người bán đàn — mà là người đã từng ở vị trí của bạn: <strong>một người mới bắt đầu hoàn toàn, không biết gì về nhạc lý</strong>, tự mò mẫm qua nhiều năm trước khi tìm được phương pháp đúng.",
      "Chính vì xuất phát điểm đó, Cường hiểu rõ những khó khăn của người mới hơn bất kỳ ai: sợ bấm đau tay, không hiểu hợp âm, không biết bắt đầu từ đâu, và hay bỏ cuộc sau vài tuần. Phương pháp giảng dạy của Cường được xây dựng để <strong>giải quyết đúng những rào cản đó</strong> — không học vẹt, không nhồi nhét lý thuyết.",
      "Với hơn 500 học viên đã đồng hành, Cường Guitar đã xây dựng được cộng đồng học guitar online và offline tại Sài Gòn — nơi học viên không chỉ học kỹ thuật mà còn kết nối với những người cùng đam mê. Nếu bạn đang tìm một người <strong>thực sự đồng hành cùng hành trình guitar của mình</strong> — đây là nơi bạn cần đến.",
    ],
    highlights: [
      "Xuất phát từ người tự học – hiểu người mới",
      "Phương pháp thực chiến, không lý thuyết khô",
      "Cộng đồng học viên 500+ tại Sài Gòn",
    ],
  },
];

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */

const SeoContentSection: React.FC = () => {
  const [expanded, setExpanded] = useState<string | null>("guitar-cu");

  return (
    <section
      aria-labelledby="seo-content-heading"
      style={{
        backgroundColor: "var(--background)",
        color: "var(--foreground)",
        fontFamily: "var(--font-roboto)",
      }}
      className="w-full py-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">

        {/* ── HEADER ── */}
        <header className="text-center mb-16">

          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-6 border"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.35)",
              color: "var(--brand-primary)",
              fontFamily: "var(--font-montserrat)",
            }}
          >
            <BookOpen className="w-4 h-4" />
            Kiến thức & Kinh nghiệm
          </div>

          {/* H2 */}
          <h2
            id="seo-content-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-5 tracking-tight"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
          >
            Mua Guitar Cũ &amp;{" "}
            <span
              className="relative inline-block"
              style={{ color: "var(--brand-primary)" }}
            >
              Học Guitar
              <span
                className="absolute left-0 -bottom-1 w-full h-0.5 rounded-full"
                style={{ backgroundColor: "var(--brand-primary)", opacity: 0.4 }}
              />
            </span>
            {" "}Tại Sài Gòn
            <span className="block text-2xl sm:text-3xl mt-2 font-bold" style={{ color: "var(--muted-foreground)" }}>
              Những Điều Cần Biết
            </span>
          </h2>

          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ color: "var(--muted-foreground)" }}
          >
            Tổng hợp kinh nghiệm thực tế từ hơn{" "}
            <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>500 học viên</strong>{" "}
            và hàng trăm cây guitar cũ đã qua tay — để bạn đưa ra quyết định đúng ngay từ đầu.
          </p>

          {/* Divider */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
          </div>
        </header>

        {/* ── MAIN LAYOUT ── */}
        <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-12 items-start">

          {/* ── LEFT: CONTENT ── */}
          <div className="space-y-4 mb-10 lg:mb-0">
            {SECTIONS.map((sec) => {
              const isOpen = expanded === sec.id;
              const Icon = sec.icon;

              return (
                <article
                  key={sec.id}
                  id={sec.id}
                  className="rounded-2xl border overflow-hidden transition-all duration-300"
                  style={{
                    borderColor: isOpen ? "rgba(200,169,110,0.45)" : "var(--border)",
                    backgroundColor: "var(--card)",
                    boxShadow: isOpen ? "0 4px 32px rgba(200,169,110,0.08)" : "none",
                  }}
                >
                  {/* Trigger */}
                  <button
                    onClick={() => setExpanded(isOpen ? null : sec.id)}
                    className="w-full flex items-center gap-4 px-6 py-5 text-left"
                    aria-expanded={isOpen}
                  >
                    {/* Tag number */}
                    <div
                      className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-sm font-black transition-all duration-200"
                      style={{
                        fontFamily: "var(--font-montserrat)",
                        backgroundColor: isOpen ? "var(--brand-primary)" : "rgba(200,169,110,0.1)",
                        color: isOpen ? "var(--brand-primary-foreground)" : "var(--brand-primary)",
                        border: isOpen ? "none" : "1.5px solid rgba(200,169,110,0.25)",
                      }}
                    >
                      {sec.tag}
                    </div>

                    {/* Icon */}
                    <Icon className="w-5 h-5 shrink-0" style={{ color: isOpen ? "var(--brand-primary)" : "var(--muted-foreground)" }} />

                    {/* Heading */}
                    <h3
                      className="flex-1 font-black text-base sm:text-lg leading-snug text-left"
                      style={{
                        fontFamily: "var(--font-montserrat)",
                        color: "var(--foreground)",
                      }}
                    >
                      {sec.heading}
                    </h3>

                    <ChevronDown
                      className="shrink-0 w-5 h-5 transition-transform duration-300"
                      style={{
                        color: "var(--brand-primary)",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </button>

                  {/* Content */}
                  {isOpen && (
                    <div style={{ borderTop: "1px solid rgba(200,169,110,0.12)" }}>
                      <div className="px-6 pt-6 pb-4 flex gap-4">
                        <div
                          className="shrink-0 w-0.5 rounded-full self-stretch"
                          style={{ backgroundColor: "rgba(200,169,110,0.35)" }}
                        />
                        <div className="space-y-4">
                          {sec.body.map((para, i) => (
                            <p
                              key={i}
                              className="text-sm sm:text-base leading-relaxed"
                              style={{ color: "var(--muted-foreground)" }}
                              dangerouslySetInnerHTML={{ __html: para }}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Highlights */}
                      <div
                        className="mx-6 mb-6 rounded-xl p-4 border"
                        style={{
                          backgroundColor: "rgba(200,169,110,0.05)",
                          borderColor: "rgba(200,169,110,0.2)",
                        }}
                      >
                        <p
                          className="text-xs font-bold uppercase tracking-widest mb-3"
                          style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
                        >
                          Điểm chính
                        </p>
                        <ul className="space-y-2">
                          {sec.highlights.map((hl, i) => (
                            <li key={i} className="flex items-center gap-2.5">
                              <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "var(--brand-primary)" }} />
                              <span
                                className="text-sm font-medium"
                                style={{ color: "var(--foreground)" }}
                              >
                                {hl}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {/* ── RIGHT: Sticky nav + CTA ── */}
          <div className="space-y-5 lg:sticky lg:top-8">

            {/* Table of contents */}
            <div
              className="rounded-3xl p-6 border"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "rgba(200,169,110,0.25)",
                boxShadow: "0 0 30px rgba(200,169,110,0.05)",
              }}
            >
              <div
                className="h-1 w-10 rounded-full mb-5"
                style={{ backgroundColor: "var(--brand-primary)" }}
              />
              <p
                className="text-xs font-bold uppercase tracking-widest mb-4"
                style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
              >
                Nội dung bài viết
              </p>
              <nav className="space-y-1">
                {SECTIONS.map((sec) => {
                  const isActive = expanded === sec.id;
                  return (
                    <button
                      key={sec.id}
                      onClick={() => setExpanded(sec.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150"
                      style={{
                        backgroundColor: isActive ? "rgba(200,169,110,0.12)" : "transparent",
                        borderLeft: isActive ? "2px solid var(--brand-primary)" : "2px solid transparent",
                      }}
                    >
                      <span
                        className="text-xs font-black shrink-0"
                        style={{
                          fontFamily: "var(--font-montserrat)",
                          color: isActive ? "var(--brand-primary)" : "var(--muted-foreground)",
                        }}
                      >
                        {sec.tag}
                      </span>
                      <span
                        className="text-xs font-semibold leading-snug"
                        style={{
                          fontFamily: "var(--font-montserrat)",
                          color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                        }}
                      >
                        {sec.heading}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* CTA card */}
            <div
              className="rounded-3xl p-6 border"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <p
                className="text-xs font-bold uppercase tracking-widest mb-3"
                style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
              >
                Sẵn sàng bắt đầu?
              </p>
              <h4
                className="text-base font-black mb-3 leading-snug"
                style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
              >
                Ghé Guitar Sài Gòn – Quận 12
              </h4>
              <p className="text-xs leading-relaxed mb-5" style={{ color: "var(--muted-foreground)" }}>
                Xem đàn trực tiếp, học thử miễn phí, tư vấn tận tâm — không áp lực mua hàng.
              </p>

              <div className="space-y-2.5">
           
                <a
                  href="/guitar-cu"
                  className="group flex items-center justify-between w-full px-4 py-3 rounded-xl font-bold text-sm border transition-all duration-200"
                  style={{
                    borderColor: "rgba(200,169,110,0.3)",
                    color: "var(--brand-primary)",
                    fontFamily: "var(--font-montserrat)",
                    backgroundColor: "rgba(200,169,110,0.05)",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(200,169,110,0.55)";
                    (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(200,169,110,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(200,169,110,0.3)";
                    (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(200,169,110,0.05)";
                  }}
                >
                  <span>Xem Guitar Cũ</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default SeoContentSection;